/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import {
  DateUtil,
  emptyPolicyHolder,
  IPolicyHolderAgeConfig,
  IPolicyHoldersInput,
} from '@nibtravel/shared/util/models';
import { format } from 'date-fns';
import { isEqual, merge } from 'lodash-es';
import { v4 as newGuid } from 'uuid';

import { IPolicyHolderStatus } from './models/policyholder.model';
import { Store } from '@ngxs/store';
import { QuoteState } from '@nibtravel/shared/data-access/quote';

@Injectable({ providedIn: 'root' })
export class PolicyHolderService {
  constructor(private store: Store) {}
  /**
   * Function created to resolve and deal with age ambiguity from
   * policy holders. We still don't have this on the screen,
   * however, the user should be asked whether a traveller that is
   * possibly ambiguous, such as age 16. In some countries, the
   * legal age to be a dependent is maximum of 16.
   * For full rationale, check http://athena.yorkstreet.local/display/KOSDEV/Ambiguous+And+Inferred+Dependent+Traveller+Ages
   *
   * TODO: Need to simplify and break this function into smaller pieces
   *
   */
  resolvePolicyHoldersAmbiguity(
    policyHolders: IPolicyHoldersInput[],
    // should be IPolicyholderConfig - which is coming from bqc module -need to replace once in common module
    policyHolderAgeConfig: IPolicyHolderAgeConfig
  ): IPolicyHoldersInput[] {
    if (!policyHolderAgeConfig) {
      return policyHolders;
    }

    const {
      maxDependentAge,
      legalAgeOfConsent,
      ambiguousAgeGap,
      maxAmbiguousAge,
    } = policyHolderAgeConfig;

    // Maximum age for automatically inferred dependents
    const minAmbiguousDependentAge = legalAgeOfConsent - 1;

    // Travellers who couldn't be dependents
    const olderTravellers = policyHolders.filter(
      (policyHolder) =>
        policyHolder.age &&
        policyHolder.age > maxDependentAge &&
        policyHolder.age <= maxAmbiguousAge
    );

    let inferredDependents: IPolicyHoldersInput[] = [];

    // set inferred travellers
    if (olderTravellers.length !== 0) {
      const oldestTraveller = olderTravellers.sort(
        (ph, ph1) => ph1.age! - ph.age!
      )[0];

      // Assumed unambiguous dependents
      inferredDependents = policyHolders.filter((policyHolder) => {
        if (
          policyHolder.age != null &&
          policyHolder.age < minAmbiguousDependentAge &&
          oldestTraveller.age! - policyHolder.age >= ambiguousAgeGap
        ) {
          policyHolder.isDependent = true; // Not good to mutate inside filter
          return true;
        }
        return undefined;
      });
    }

    // Set Ambiguities
    policyHolders.forEach((policyHolder) => {
      policyHolder.isPrimary = false;
      const isLessThanMaxDependentAge =
        policyHolder.age != null && policyHolder.age <= maxDependentAge;
      const isNotInferredDependent = !inferredDependents.some((ph) =>
        isEqual(ph, policyHolder)
      );
      const anotherTravellerCouldBeParent = policyHolders.some(
        (otherPolicyHolder) => {
          return (
            otherPolicyHolder.age != null &&
            policyHolder.age != null &&
            otherPolicyHolder !== policyHolder &&
            otherPolicyHolder.age >= legalAgeOfConsent &&
            otherPolicyHolder.age <= maxAmbiguousAge &&
            otherPolicyHolder.age - policyHolder.age >= ambiguousAgeGap
          );
        }
      );
      if (
        isLessThanMaxDependentAge &&
        isNotInferredDependent &&
        anotherTravellerCouldBeParent
      ) {
        policyHolder.isAmbiguous = true;
      } else {
        policyHolder.isAmbiguous = false;
        policyHolder.isDependent = !!policyHolder.isDependent;
      }
      // if dependentAge exists (get from the url in standalone qqc)
      const dependentAge = this.store.selectSnapshot(
        QuoteState.getPolicyDependentAge
      );
      if (dependentAge?.includes(String(policyHolder.age))) {
        policyHolder.isDependent = true;
      }
    });

    // Set the first non-dependent, non-ambiguous policy holder as the primary
    // policy holder
    for (const ph of policyHolders) {
      if (!ph.isDependent && !ph.isAmbiguous) {
        ph.isPrimary = true;
        break;
      }
    }

    return policyHolders;
  }

  /**
   * The initial state to capture the policy holder information starts at the QQC.
   * However, at the QQC, only age is capture and the product api requires more data
   * so it can produce the quote itself. This method gets a base empty policy holder
   * and merge with the qqc input and resolves the ambiguity logic.
   *
   */
  formatPolicyHolders(
    input: IPolicyHoldersInput[],
    config: IPolicyHolderAgeConfig
  ): IPolicyHoldersInput[] {
    input = input.map((ph) => this.handlePolicyHolder(ph));
    return this.resolvePolicyHoldersAmbiguity(input, config);
  }

  /** Update the age ambiguity for policy holders */
  updateAmbiguity(
    input: IPolicyHoldersInput[],
    isDependent: string,
    guid: string
  ): IPolicyHoldersInput[] {
    return input.map((ph) =>
      ph.guid !== guid
        ? ph
        : {
            ...ph,
            isAmbiguous: false,
            isDependent: isDependent === 'true',
          }
    );
  }

  canGetQuote(
    policyHolders: IPolicyHoldersInput[],
    config: IPolicyHolderAgeConfig
  ): IPolicyHolderStatus {
    const isAmbiguous = this.checkAmbiguity(policyHolders);
    const countError =
      !this.ageExist(policyHolders) || !!this.countError(policyHolders, config);
    return {
      isAmbiguous: isAmbiguous,
      countError: countError,
    };
  }

  getValidPolicyholder(input: IPolicyHoldersInput[]): IPolicyHoldersInput[] {
    const filteredInput = input.filter((ph) => ph.age != null);
    return this.ensureDobForm(filteredInput);
  }

  /**
   * The API expects a certain date format, which is usually different
   * from display format and this function ensures the quote uses the dob
   * in the correct format
   */
  ensureDobForm(sortedInput: IPolicyHoldersInput[]): IPolicyHoldersInput[] {
    return sortedInput.map((ph) =>
      !ph.dateOfBirth
        ? ph
        : {
            ...ph,
            dateOfBirth: format(
              new Date(ph.dateOfBirth as string),
              DateUtil.IsoDateFormat
            ),
          }
    );
  }

  countError(
    policyHolders: IPolicyHoldersInput[],
    config: IPolicyHolderAgeConfig
  ): undefined | string {
    const adultTraveller = policyHolders.filter(
      (ph) =>
        ph.age != null && ph.isDependent === false && ph.isAmbiguous === false
    );

    const dependantTraveller = policyHolders.filter(
      (ph) =>
        ph.age != null && ph.isDependent === true && ph.isAmbiguous === false
    );

    const oldestAdult = adultTraveller.sort((ph, ph1) => ph1.age! - ph.age!)[0];

    if (
      adultTraveller.length > config.maxTravellers &&
      oldestAdult.age! <= config.maxDependentAge
    ) {
      return `We are unable to provide a quote for more than ${config.maxTravellers} dependent travellers`;
    }

    if (
      adultTraveller.length > config.maxTravellers ||
      dependantTraveller.length > config.maxDependents
    ) {
      return `We are unable to provide a quote for more than
      ${config.maxTravellers} adult traveller(s) and more than
      ${config.maxDependents} dependent traveller(s).`;
    }
    return undefined;
  }

  public checkAmbiguity(policyHolders: IPolicyHoldersInput[]): boolean {
    return policyHolders.some((ph) => ph.isAmbiguous === true);
  }

  private ageExist(policyHolders: IPolicyHoldersInput[]): boolean {
    return policyHolders.some((ph) => ph.age != null);
  }

  private handlePolicyHolder(policyHolder: IPolicyHoldersInput) {
    policyHolder = merge({}, emptyPolicyHolder, policyHolder);
    // Add new unique id to the policyHolder
    if (!policyHolder.guid || this.isEmptyUuid(policyHolder.guid)) {
      policyHolder.guid = newGuid();
    }

    policyHolder.age = this.coerceAgeOrNull(policyHolder.age);
    return policyHolder;
  }

  private isEmptyUuid(value: string): boolean {
    return value === '00000000-0000-0000-0000-000000000000';
  }

  private coerceAgeOrNull(age: string | number | null) {
    if (typeof age === 'number') {
      return age;
    }

    if (age == null || age === '') {
      return null;
    }

    const result = Number.parseInt(age);
    return isNaN(result) ? null : result;
  }
}
